import type { AutomationDefinition } from '../../../types/automation'
import type { DesignAsset } from '../../../types/design'
import type { FormDefinition } from '../../../types/forms'
import type { MeetingRecord } from '../../../types/meetings'
import type { GeneratedReportRecord } from '../../../types/report'
import type { CanvasElement, ChecklistItem, TimelineBoardKey, WorkspaceFileMetadata } from '../../../types/schema'
import type { WorkspaceAction } from '../../../utils/workspacePermissions'
import { moduleForLayoutView, type ModuleKey } from '../../../utils/workspaceModules'
import type { LineExecuteActionKind } from '../execute/lineExecuteKindMap'
import { isPageHiddenForMe } from '../../pageVisibilityService'

export type AuthenticatedWebSurface =
  | 'DASHBOARD'
  | 'CALENDAR'
  | 'EXPLORE'
  | 'USER_SETTINGS'
  | 'YOUR_PAGE'
  | 'PURCHASE'
  | 'INVITES'
  | 'LINK'
  | 'STOCK'
  | 'CLIENTS'
  | 'TIMESHEETS'
  | 'BUDGET'
  | 'QUOTA'
  | 'TEACHING'
  | 'TRAINING'
  | 'CONSTRUCTION'
  | 'HOSPITAL'
  | 'SITES'
  | 'DELIVERY'
  | 'SHOPS'
  | 'STAYS'
  | 'JOBS'
  | 'CLINICS'
  | 'PLANT'
  | 'UPLOAD'
  | 'FORMS'
  | 'MEETINGS'
  | 'AUTOMATE'
  | 'DESIGN'
  | 'AI_MAGIC'
  | 'CHAT'
  | 'CHECKLISTS'
  | 'MANAGE'
  | 'RAID'
  | 'FOR_YOU'
  | 'FOCUS'
  | 'WORKSPACE_SETTINGS'
  | 'SUMMARY'
  | 'OVERVIEW'
  | 'ANALYSIS'
  | 'REPORTS'

export type WebIntentFamily =
  | 'NAVIGATION'
  | 'TIMELINE'
  | 'CHECKLIST'
  | 'FORM'
  | 'AUTOMATION'
  | 'MEETING'
  | 'CHAT'
  | 'MEMBER'
  | 'REPORT'
  | 'UPLOAD'
  | 'DESIGN'
  | 'RESOURCE'
  | 'WORKSPACE'
  | 'AI'
  | 'TOUR'
  | 'EXPERIENCE'

export type WebIntentCapabilityStatus = 'supported' | 'preview_only' | 'planned'

export type WebIntentRisk = 'READ_ONLY' | 'LOW' | 'MEDIUM' | 'HIGH' | 'RESTRICTED'

export type WebIntentConfirmationPolicy =
  | 'NONE'
  | 'CONFIRM_BEFORE_RUN'
  | 'CONFIRM_WITH_REVIEW'
  | 'SURFACE_APPROVAL'

export type WebIntentConsentMode =
  | 'ask_every_time'
  | 'allow_once'
  | 'allow_for_this_target'
  | 'allow_for_this_target_type'
  | 'allow_until_revoked'

export type WebIntentTargetType =
  | 'WEB_ROUTE'
  | 'WORKSPACE'
  | 'BOARD'
  | 'TIMELINE_NODE'
  | 'CHECKLIST_ITEM'
  | 'FORM'
  | 'FORM_RESPONSE'
  | 'AUTOMATION'
  | 'MEETING'
  | 'CHAT_CHANNEL'
  | 'REPORT'
  | 'UPLOAD_FILE'
  | 'DESIGN_ASSET'
  | 'STOCK_ITEM'
  | 'BUDGET_ITEM'
  | 'QUOTA'
  | 'RAID'
  | 'FOCUS_SESSION'
  | 'USER_PROFILE'
  | 'PURCHASE'
  | 'TOUR'

export type WebIntentSource = 'STATIC_CATALOG' | 'WORKSPACE_DATA'

export interface WebIntentDefinition {
  id: string
  title: string
  description: string
  family: WebIntentFamily
  surface: AuthenticatedWebSurface
  source: WebIntentSource
  routePatterns: string[]
  verbs: string[]
  examples: string[]
  matcherHints: string[]
  targetTypes: WebIntentTargetType[]
  risk: WebIntentRisk
  status: WebIntentCapabilityStatus
  confirmation: WebIntentConfirmationPolicy
  consentModes: WebIntentConsentMode[]
  requiredAllPermissions?: WorkspaceAction[]
  requiredAnyPermissions?: WorkspaceAction[]
  targetRef?: WebIntentTargetRef
  availability?: WebIntentAvailability
  executeActionKind?: LineExecuteActionKind
  executorHint: string
  tourFallback: boolean
}

export interface WebIntentTargetRef {
  id: string
  type: WebIntentTargetType
  title: string
  status?: string | null
  route?: string
  board?: TimelineBoardKey
  parentId?: string | null
}

export interface WebIntentAvailability {
  state: 'available' | 'needs_permission' | 'needs_target' | 'unavailable' | 'unsupported'
  message: string
  missingPermissions?: WorkspaceAction[]
}

export interface WebIntentPermissionContext {
  canPerform: (permission: WorkspaceAction) => boolean
  switchedOffModules?: ReadonlySet<ModuleKey>
  isPageHidden?: (surface: string) => boolean
}

export interface WorkspaceIntentChatChannel {
  id: string
  name: string
  archived?: boolean
  isReadable?: boolean
}

export interface WorkspaceIntentResourceItem {
  id: string
  title: string
  kind: 'STOCK_ITEM' | 'BUDGET_ITEM' | 'QUOTA'
  status?: string | null
}

export interface WorkspaceDynamicIntentInput {
  workspaceId: string
  workspaceSlug?: string | null
  board?: TimelineBoardKey
  timelineElements?: Array<Pick<CanvasElement, 'id' | 'title' | 'type' | 'board' | 'parentId'>>
  checklistItems?: Array<Pick<ChecklistItem, 'id' | 'title' | 'parentContainerId' | 'board' | 'workflowStatus' | 'isCompleted'>>
  forms?: Array<Pick<FormDefinition, 'id' | 'title' | 'lifecycleStatus' | 'isLocked'>>
  automations?: Array<Pick<AutomationDefinition, 'id' | 'name' | 'status' | 'kind'>>
  meetings?: Array<Pick<MeetingRecord, 'id' | 'title' | 'status'>>
  reports?: Array<Pick<GeneratedReportRecord, 'id' | 'title' | 'revokedAt'>>
  uploads?: Array<Pick<WorkspaceFileMetadata, 'id' | 'fileName' | 'status' | 'visibility' | 'artifactType'>>
  designAssets?: Array<Pick<DesignAsset, 'id' | 'title' | 'mode' | 'boundIntent' | 'boundOutputStatus'>>
  chatChannels?: WorkspaceIntentChatChannel[]
  resources?: WorkspaceIntentResourceItem[]
}

export const AUTHENTICATED_WEB_SURFACES: AuthenticatedWebSurface[] = [
  'DASHBOARD',
  'CALENDAR',
  'EXPLORE',
  'USER_SETTINGS',
  'YOUR_PAGE',
  'PURCHASE',
  'INVITES',
  'LINK',
  'STOCK',
  'CLIENTS',
  'TIMESHEETS',
  'BUDGET',
  'QUOTA',
  'TEACHING',
  'TRAINING',
  'CONSTRUCTION',
  'HOSPITAL',
  'SITES',
  'DELIVERY',
  'SHOPS',
  'STAYS',
  'JOBS',
  'CLINICS',
  'PLANT',
  'UPLOAD',
  'FORMS',
  'MEETINGS',
  'AUTOMATE',
  'DESIGN',
  'AI_MAGIC',
  'CHAT',
  'CHECKLISTS',
  'MANAGE',
  'RAID',
  'FOR_YOU',
  'FOCUS',
  'WORKSPACE_SETTINGS',
  'SUMMARY',
  'OVERVIEW',
  'ANALYSIS',
  'REPORTS',
]

export const EXCLUDED_WEB_INTENT_ROUTE_PREFIXES = [
  '/',
  '/pricing',
  '/docs',
  '/download',
  '/about',
  '/member',
  '/helper',
  '/sole-con-mana-ment-ge',
] as const

export const WEB_SURFACE_ROUTE_PATTERNS: Record<AuthenticatedWebSurface, string[]> = {
  DASHBOARD: ['/console/workspace/:slug/:board', '/console/personal/d'],
  CALENDAR: ['/console/workspace/:slug/calendar'],
  EXPLORE: ['/console/explore', '/console/explore/:category'],
  USER_SETTINGS: ['/console/settings'],
  YOUR_PAGE: ['/console/your'],
  PURCHASE: ['/console/purchase', '/console/purchase/storage'],
  INVITES: ['/console/team', '/console/members', '/console/invites'],
  LINK: ['/console/workspace/:slug/link'],
  STOCK: ['/console/workspace/:slug/stock'],
  CLIENTS: ['/console/workspace/:slug/clients'],
  TIMESHEETS: ['/console/workspace/:slug/timesheets'],
  BUDGET: ['/console/workspace/:slug/budget'],
  QUOTA: ['/console/workspace/:slug/quota'],
  TEACHING: ['/console/workspace/:slug/teaching'],
  TRAINING: ['/console/workspace/:slug/training'],
  CONSTRUCTION: ['/console/workspace/:slug/construction'],
  HOSPITAL: ['/console/workspace/:slug/hospital'],
  SITES: ['/console/workspace/:slug/sites'],
  DELIVERY: ['/console/workspace/:slug/delivery'],
  SHOPS: ['/console/workspace/:slug/shops'],
  STAYS: ['/console/workspace/:slug/stays'],
  JOBS: ['/console/workspace/:slug/jobs'],
  CLINICS: ['/console/workspace/:slug/clinics'],
  PLANT: ['/console/workspace/:slug/plant'],
  UPLOAD: ['/console/workspace/:slug/upload'],
  FORMS: ['/console/workspace/:slug/forms'],
  MEETINGS: ['/console/workspace/:slug/meetings'],
  AUTOMATE: ['/console/workspace/:slug/automate'],
  DESIGN: ['/console/workspace/:slug/design'],
  AI_MAGIC: ['/console/workspace/:slug/:board/ai'],
  CHAT: ['/console/workspace/:slug/chat'],
  CHECKLISTS: ['/console/workspace/:slug/:board/checklists'],
  MANAGE: ['/console/workspace/:slug/:board/manage', '/console/personal/d/manage'],
  RAID: ['/console/workspace/:slug/:board/raid'],
  FOR_YOU: ['/console/workspace/:slug/for-you'],
  FOCUS: ['/console/focus'],
  WORKSPACE_SETTINGS: ['/console/workspace/:slug/:board/settings/:role', '/console/personal/settings'],
  SUMMARY: ['/console/workspace/:slug/manage/audit'],
  OVERVIEW: ['/console/workspace/:slug/overview'],
  ANALYSIS: ['/console/workspace/:slug/analysis'],
  REPORTS: ['/console/workspace/:slug/:board/reports'],
}

const NAVIGATION_PERMISSION_BY_SURFACE: Partial<Record<AuthenticatedWebSurface, WorkspaceAction[]>> = {
  DASHBOARD: ['timeline.view'],
  CALENDAR: ['timeline.view'],
  LINK: ['integrations.view'],
  UPLOAD: ['integrations.view'],
  FORMS: ['forms.view'],
  MEETINGS: ['meetings.view'],
  AUTOMATE: ['automation.view'],
  DESIGN: ['forms.view'],
  AI_MAGIC: ['ai.use'],
  CHAT: ['chat.view'],
  CHECKLISTS: ['timeline.view'],
  MANAGE: ['timeline.edit'],
  RAID: ['raid.participate'],
  FOR_YOU: ['timeline.view'],
  WORKSPACE_SETTINGS: ['workspace.view_settings'],
  SUMMARY: ['workspace.view_reports'],
  OVERVIEW: ['overview.view'],
  ANALYSIS: ['workspace.view_analysis', 'readonly.view_analysis'],
  REPORTS: ['workspace.view_reports'],
  TEACHING: ['timeline.view'],
  TRAINING: ['timeline.view'],
}

const READ_ONLY_CONSENT: WebIntentConsentMode[] = ['ask_every_time']
const MUTATION_CONSENT: WebIntentConsentMode[] = [
  'ask_every_time',
  'allow_once',
  'allow_for_this_target',
  'allow_for_this_target_type',
  'allow_until_revoked',
]

const surfaceTitle = (surface: AuthenticatedWebSurface) =>
  surface.toLowerCase().split('_').map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ')

const routeIntentForSurface = (surface: AuthenticatedWebSurface): WebIntentDefinition => ({
  id: `navigate.${surface.toLowerCase()}`,
  title: `Open ${surfaceTitle(surface)}`,
  description: `Navigate to the authenticated ${surfaceTitle(surface)} web surface.`,
  family: 'NAVIGATION',
  surface,
  source: 'STATIC_CATALOG',
  routePatterns: WEB_SURFACE_ROUTE_PATTERNS[surface],
  verbs: ['open', 'go to', 'show', 'navigate', 'jump to'],
  examples: [`Open ${surfaceTitle(surface)}`],
  matcherHints: [surfaceTitle(surface), ...WEB_SURFACE_ROUTE_PATTERNS[surface]],
  targetTypes: ['WEB_ROUTE'],
  risk: 'READ_ONLY',
  status: 'supported',
  confirmation: 'NONE',
  consentModes: READ_ONLY_CONSENT,
  requiredAnyPermissions: NAVIGATION_PERMISSION_BY_SURFACE[surface],
  executeActionKind: 'NAVIGATION',
  executorHint: 'Navigate through the existing console route helpers and dirty-navigation guards.',
  tourFallback: false,
})

const staticIntent = (
  intent: Omit<WebIntentDefinition, 'source' | 'routePatterns' | 'consentModes'> & { routePatterns?: string[]; consentModes?: WebIntentConsentMode[] },
): WebIntentDefinition => ({
  source: 'STATIC_CATALOG',
  routePatterns: WEB_SURFACE_ROUTE_PATTERNS[intent.surface],
  consentModes: intent.risk === 'READ_ONLY' ? READ_ONLY_CONSENT : MUTATION_CONSENT,
  ...intent,
})

const STATIC_ACTION_INTENTS: WebIntentDefinition[] = [
  staticIntent({
    id: 'timeline.create_goal',
    title: 'Create goal',
    description: 'Create a new goal on the active board.',
    family: 'TIMELINE',
    surface: 'DASHBOARD',
    verbs: ['create', 'add', 'make', 'start'],
    examples: ['Create a goal called Launch prep'],
    matcherHints: ['goal', 'new goal', 'timeline'],
    targetTypes: ['BOARD', 'TIMELINE_NODE'],
    risk: 'MEDIUM',
    status: 'supported',
    confirmation: 'CONFIRM_BEFORE_RUN',
    requiredAllPermissions: ['timeline.create'],
    executorHint: 'Use existing timeline creation or Automate CREATE_NODE paths; never write Firestore directly from AI.',
    tourFallback: true,
  }),
  staticIntent({
    id: 'timeline.edit_selected',
    title: 'Edit selected timeline item',
    description: 'Prepare a change for an existing goal, task, or subtask.',
    family: 'TIMELINE',
    surface: 'MANAGE',
    verbs: ['edit', 'rename', 'update', 'move', 'change'],
    examples: ['Rename this task', 'Move this task to in progress'],
    matcherHints: ['selected task', 'selected goal', 'status', 'date'],
    targetTypes: ['TIMELINE_NODE'],
    risk: 'MEDIUM',
    status: 'supported',
    confirmation: 'CONFIRM_BEFORE_RUN',
    requiredAllPermissions: ['timeline.edit'],
    executeActionKind: 'TIMELINE_STATUS_CHANGE',
    executorHint: 'Route through Manage drafts, DeltaProcessor, or trusted Automate action bundles.',
    tourFallback: true,
  }),
  staticIntent({
    id: 'checklist.create_item',
    title: 'Create checklist item',
    description: 'Create a checklist item under a selected goal, task, or subtask.',
    family: 'CHECKLIST',
    surface: 'CHECKLISTS',
    verbs: ['create', 'add', 'make'],
    examples: ['Create a checklist item called Confirm venue access'],
    matcherHints: ['checklist item', 'checklist node'],
    targetTypes: ['CHECKLIST_ITEM', 'TIMELINE_NODE'],
    risk: 'MEDIUM',
    status: 'supported',
    confirmation: 'CONFIRM_BEFORE_RUN',
    requiredAllPermissions: ['checklist.create'],
    executorHint: 'Use existing checklist creation paths or Automate CREATE_CHECKLIST_ITEM.',
    tourFallback: true,
  }),
  staticIntent({
    id: 'checklist.change_status',
    title: 'Change checklist status',
    description: 'Move checklist work through its workflow or completion status.',
    family: 'CHECKLIST',
    surface: 'CHECKLISTS',
    verbs: ['mark', 'complete', 'reopen', 'set', 'change'],
    examples: ['Mark this checklist complete'],
    matcherHints: ['workflow status', 'done', 'proof', 'blocked'],
    targetTypes: ['CHECKLIST_ITEM'],
    risk: 'MEDIUM',
    status: 'supported',
    confirmation: 'CONFIRM_BEFORE_RUN',
    requiredAllPermissions: ['checklist.change_status'],
    executeActionKind: 'CHECKLIST_TOGGLE_DONE',
    executorHint: 'Route through checklist operational permissions and DeltaProcessor-backed status paths.',
    tourFallback: true,
  }),
  staticIntent({
    id: 'forms.create',
    title: 'Create form',
    description: 'Create a new workspace form definition.',
    family: 'FORM',
    surface: 'FORMS',
    verbs: ['create', 'build', 'make', 'draft'],
    examples: ['Create a vendor intake form'],
    matcherHints: ['form', 'intake', 'fields'],
    targetTypes: ['FORM'],
    risk: 'MEDIUM',
    status: 'preview_only',
    confirmation: 'SURFACE_APPROVAL',
    requiredAllPermissions: ['forms.design'],
    executorHint: 'Prepare a form draft and open Forms builder for final review.',
    tourFallback: true,
  }),
  staticIntent({
    id: 'automation.create',
    title: 'Create automation',
    description: 'Create a paused automation draft from a natural-language workflow.',
    family: 'AUTOMATION',
    surface: 'AUTOMATE',
    verbs: ['automate', 'create automation', 'set up workflow', 'whenever', 'when'],
    examples: ['Create an automation that notifies me when proof is uploaded'],
    matcherHints: ['automation', 'workflow', 'trigger', 'action'],
    targetTypes: ['AUTOMATION'],
    risk: 'MEDIUM',
    status: 'supported',
    confirmation: 'CONFIRM_WITH_REVIEW',
    requiredAllPermissions: ['automation.manage'],
    executorHint: 'Use compileAutomationDraftFromPrompt and createAutomationGraph; drafts remain paused.',
    tourFallback: true,
  }),
  staticIntent({
    id: 'meetings.schedule',
    title: 'Schedule meeting',
    description: 'Prepare a meeting draft with participants, time, and agenda.',
    family: 'MEETING',
    surface: 'MEETINGS',
    verbs: ['schedule', 'book', 'set up', 'arrange'],
    examples: ['Schedule a huddle with Priya tomorrow at 10am'],
    matcherHints: ['meeting', 'huddle', 'sync', 'call'],
    targetTypes: ['MEETING'],
    risk: 'MEDIUM',
    status: 'supported',
    confirmation: 'CONFIRM_WITH_REVIEW',
    requiredAllPermissions: ['meetings.schedule'],
    executeActionKind: 'MEETING_SCHEDULE',
    executorHint: 'Use the meeting concierge and Meetings surface for final invite approval.',
    tourFallback: true,
  }),
  staticIntent({
    id: 'chat.send',
    title: 'Send chat message',
    description: 'Post a message into a workspace chat channel.',
    family: 'CHAT',
    surface: 'CHAT',
    verbs: ['send', 'post', 'message', 'tell'],
    examples: ['Post "ETA is 4pm" in #ops'],
    matcherHints: ['chat', 'channel', 'message'],
    targetTypes: ['CHAT_CHANNEL'],
    risk: 'MEDIUM',
    status: 'preview_only',
    confirmation: 'CONFIRM_BEFORE_RUN',
    requiredAllPermissions: ['chat.send'],
    executeActionKind: 'CHAT_SEND',
    executorHint: 'Resolve the channel and use existing ChatContext/service send path.',
    tourFallback: true,
  }),
  staticIntent({
    id: 'members.invite',
    title: 'Invite member or helper',
    description: 'Invite a person into the workspace with a role-specific path.',
    family: 'MEMBER',
    surface: 'INVITES',
    verbs: ['invite', 'add member', 'add helper'],
    examples: ['Invite alex@example.com as helper'],
    matcherHints: ['email invite', 'phone invite', 'helper'],
    targetTypes: ['USER_PROFILE'],
    risk: 'HIGH',
    status: 'supported',
    confirmation: 'CONFIRM_BEFORE_RUN',
    requiredAnyPermissions: ['workspace.manage_invites', 'workspace.manage_field_passes'],
    executeActionKind: 'MEMBER_INVITE',
    executorHint: 'Use existing invite callables/services; never write membership rows directly.',
    tourFallback: true,
  }),
  staticIntent({
    id: 'reports.generate',
    title: 'Generate report',
    description: 'Generate a workspace report from the Reports surface.',
    family: 'REPORT',
    surface: 'REPORTS',
    verbs: ['generate', 'create', 'build'],
    examples: ['Generate a weekly progress report'],
    matcherHints: ['report', 'summary', 'audit', 'weekly'],
    targetTypes: ['REPORT'],
    risk: 'MEDIUM',
    status: 'preview_only',
    confirmation: 'SURFACE_APPROVAL',
    requiredAllPermissions: ['workspace.view_reports'],
    executeActionKind: 'REPORT_PREVIEW',
    executorHint: 'Open the report wizard with a prepared configuration; report generation keeps its wallet gate.',
    tourFallback: true,
  }),
  staticIntent({
    id: 'uploads.add_files',
    title: 'Upload files',
    description: 'Open Upload Hub for workspace file upload.',
    family: 'UPLOAD',
    surface: 'UPLOAD',
    verbs: ['upload', 'add file', 'attach'],
    examples: ['Upload the new permit PDF'],
    matcherHints: ['file', 'upload', 'artifact'],
    targetTypes: ['UPLOAD_FILE'],
    risk: 'LOW',
    status: 'preview_only',
    confirmation: 'SURFACE_APPROVAL',
    requiredAllPermissions: ['integrations.view'],
    executorHint: 'Open Upload Hub; browser file selection remains user-driven.',
    tourFallback: true,
  }),
  staticIntent({
    id: 'design.create',
    title: 'Create design asset',
    description: 'Create a workspace design asset or bound design blueprint.',
    family: 'DESIGN',
    surface: 'DESIGN',
    verbs: ['create', 'draw', 'design', 'make'],
    examples: ['Create a form layout design'],
    matcherHints: ['design', 'canvas', 'blueprint', 'bound'],
    targetTypes: ['DESIGN_ASSET'],
    risk: 'MEDIUM',
    status: 'preview_only',
    confirmation: 'SURFACE_APPROVAL',
    requiredAllPermissions: ['forms.view'],
    executorHint: 'Open Design Hub with a draft; materialization must use existing design materializers.',
    tourFallback: true,
  }),
  staticIntent({
    id: 'link.sync',
    title: 'Run integration sync',
    description: 'Queue a manual sync for a connected provider.',
    family: 'WORKSPACE',
    surface: 'LINK',
    verbs: ['sync', 'refresh', 'recheck'],
    examples: ['Sync Google Drive now'],
    matcherHints: ['integration', 'provider', 'link hub'],
    targetTypes: ['WORKSPACE'],
    risk: 'MEDIUM',
    status: 'preview_only',
    confirmation: 'CONFIRM_BEFORE_RUN',
    requiredAllPermissions: ['integrations.sync'],
    executorHint: 'Use Link Hub connection readiness and manual sync callables.',
    tourFallback: true,
  }),
  staticIntent({
    id: 'resources.manage_stock',
    title: 'Manage stock',
    description: 'Create or update stock inventory records.',
    family: 'RESOURCE',
    surface: 'STOCK',
    verbs: ['add stock', 'update stock', 'scan inventory'],
    examples: ['Update the plywood stock count'],
    matcherHints: ['inventory', 'stock', 'sku'],
    targetTypes: ['STOCK_ITEM'],
    risk: 'MEDIUM',
    status: 'planned',
    confirmation: 'SURFACE_APPROVAL',
    requiredAllPermissions: ['timeline.edit'],
    executorHint: 'Use Stock surface/service permissions once a dedicated intent adapter exists.',
    tourFallback: true,
  }),
  staticIntent({
    id: 'resources.manage_budget',
    title: 'Manage budget',
    description: 'Create or update budget lines and expense records.',
    family: 'RESOURCE',
    surface: 'BUDGET',
    verbs: ['add expense', 'update budget', 'log cost'],
    examples: ['Log a 500 material expense'],
    matcherHints: ['budget', 'expense', 'cost'],
    targetTypes: ['BUDGET_ITEM'],
    risk: 'MEDIUM',
    status: 'planned',
    confirmation: 'SURFACE_APPROVAL',
    requiredAllPermissions: ['timeline.edit'],
    executorHint: 'Use Budget surface/service permissions once a dedicated intent adapter exists.',
    tourFallback: true,
  }),
  staticIntent({
    id: 'resources.manage_quota',
    title: 'Manage quota',
    description: 'Create or update quota targets.',
    family: 'RESOURCE',
    surface: 'QUOTA',
    verbs: ['set quota', 'update quota', 'track target'],
    examples: ['Set a quota for 20 completed installs'],
    matcherHints: ['quota', 'target', 'metric'],
    targetTypes: ['QUOTA'],
    risk: 'MEDIUM',
    status: 'planned',
    confirmation: 'SURFACE_APPROVAL',
    requiredAllPermissions: ['timeline.edit'],
    executorHint: 'Use Quota surface/service permissions once a dedicated intent adapter exists.',
    tourFallback: true,
  }),
  staticIntent({
    id: 'workspace.interactive_tour',
    title: 'Show interactive tour',
    description: 'Generate or replay a guided tour for an authenticated web-console workflow.',
    family: 'TOUR',
    surface: 'AI_MAGIC',
    verbs: ['show me how', 'walk me through', 'guide me', 'tour'],
    examples: ['Show me how to create an automation'],
    matcherHints: ['tour', 'guide', 'walkthrough', 'show me how'],
    targetTypes: ['TOUR', 'WEB_ROUTE'],
    risk: 'LOW',
    status: 'supported',
    confirmation: 'NONE',
    requiredAllPermissions: ['ai.use'],
    executorHint: 'Use the existing TourGuide generation/registration path and sanitizer.',
    tourFallback: false,
  }),
]

export const WEB_INTENT_CATALOG: WebIntentDefinition[] = [
  ...AUTHENTICATED_WEB_SURFACES.map(routeIntentForSurface),
  ...STATIC_ACTION_INTENTS,
]

const slugPart = (input: WorkspaceDynamicIntentInput) =>
  input.workspaceSlug?.trim() ? `/console/workspace/${input.workspaceSlug.trim()}` : '/console/workspace/:slug'

const BOARD_ROUTE_SEGMENT: Record<TimelineBoardKey, string> = {
  DASHBOARD: 'd',
  VISIONBOARD: 'visionboard',
  ARCHIVE: 'archive',
  BIN: 'bin',
}

const boardRouteSegment = (board: TimelineBoardKey | undefined) =>
  BOARD_ROUTE_SEGMENT[board ?? 'DASHBOARD']

const activeBoard = (input: WorkspaceDynamicIntentInput) => boardRouteSegment(input.board)

const itemIntent = (
  input: WorkspaceDynamicIntentInput,
  intent: Omit<WebIntentDefinition, 'source' | 'routePatterns' | 'consentModes'> & { routePatterns?: string[]; consentModes?: WebIntentConsentMode[] },
): WebIntentDefinition => ({
  source: 'WORKSPACE_DATA',
  routePatterns: intent.routePatterns ?? WEB_SURFACE_ROUTE_PATTERNS[intent.surface],
  consentModes: intent.consentModes ?? (intent.risk === 'READ_ONLY' ? READ_ONLY_CONSENT : MUTATION_CONSENT),
  ...intent,
  matcherHints: [input.workspaceId, ...intent.matcherHints],
})

const dynamicAvailability = (
  available: boolean,
  message: string,
): WebIntentAvailability => available
  ? { state: 'available', message }
  : { state: 'unavailable', message }

export const buildWorkspaceDynamicIntentCatalog = (
  input: WorkspaceDynamicIntentInput,
): WebIntentDefinition[] => {
  const base = slugPart(input)
  const board = activeBoard(input)
  const intents: WebIntentDefinition[] = []

  for (const element of input.timelineElements ?? []) {
    const route = `${base}/${boardRouteSegment(element.board ?? input.board)}`
    const targetRef: WebIntentTargetRef = {
      id: element.id,
      type: 'TIMELINE_NODE',
      title: element.title,
      board: element.board,
      parentId: element.parentId,
      route,
    }
    intents.push(
      itemIntent(input, {
        id: `timeline.${element.id}.open`,
        title: `Open ${element.title}`,
        description: `Open ${element.type.toLowerCase()} "${element.title}".`,
        family: 'TIMELINE',
        surface: 'DASHBOARD',
        verbs: ['open', 'show', 'inspect'],
        examples: [`Open ${element.title}`],
        matcherHints: [element.title, element.type],
        targetTypes: ['TIMELINE_NODE'],
        targetRef,
        risk: 'READ_ONLY',
        status: 'supported',
        confirmation: 'NONE',
        requiredAllPermissions: ['timeline.view'],
        executeActionKind: 'NAVIGATION',
        executorHint: 'Navigate to the item route or select it on the current board.',
        tourFallback: false,
      }),
      itemIntent(input, {
        id: `timeline.${element.id}.edit`,
        title: `Edit ${element.title}`,
        description: `Edit ${element.type.toLowerCase()} "${element.title}" through the governed timeline path.`,
        family: 'TIMELINE',
        surface: 'MANAGE',
        verbs: ['edit', 'rename', 'update', 'move', 'change status'],
        examples: [`Update ${element.title}`],
        matcherHints: [element.title, element.type, 'timeline edit'],
        targetTypes: ['TIMELINE_NODE'],
        targetRef,
        risk: 'MEDIUM',
        status: 'supported',
        confirmation: 'CONFIRM_BEFORE_RUN',
        requiredAllPermissions: ['timeline.edit'],
        executorHint: 'Use Manage/DeltaProcessor/Automate action paths for the selected record.',
        tourFallback: true,
      }),
    )
  }

  for (const item of input.checklistItems ?? []) {
    const title = item.title || item.id
    const targetRef: WebIntentTargetRef = {
      id: item.id,
      type: 'CHECKLIST_ITEM',
      title,
      status: item.workflowStatus ?? (item.isCompleted ? 'COMPLETED' : null),
      board: item.board,
      parentId: item.parentContainerId,
      route: `${base}/${board}/checklists`,
    }
    intents.push(
      itemIntent(input, {
        id: `checklist.${item.id}.open`,
        title: `Open ${title}`,
        description: `Open checklist item "${title}".`,
        family: 'CHECKLIST',
        surface: 'CHECKLISTS',
        verbs: ['open', 'show', 'inspect'],
        examples: [`Open checklist ${title}`],
        matcherHints: [title, 'checklist'],
        targetTypes: ['CHECKLIST_ITEM'],
        targetRef,
        risk: 'READ_ONLY',
        status: 'supported',
        confirmation: 'NONE',
        requiredAllPermissions: ['timeline.view'],
        executeActionKind: 'NAVIGATION',
        executorHint: 'Open the checklist detail surface for this item.',
        tourFallback: false,
      }),
      itemIntent(input, {
        id: `checklist.${item.id}.work`,
        title: `Update ${title}`,
        description: `Change status, proof, assignment, comments, or dependency metadata for "${title}" when permitted.`,
        family: 'CHECKLIST',
        surface: 'CHECKLISTS',
        verbs: ['complete', 'reopen', 'assign', 'comment', 'upload proof', 'link dependency'],
        examples: [`Mark ${title} complete`],
        matcherHints: [title, 'status', 'proof', 'assignee', 'dependency'],
        targetTypes: ['CHECKLIST_ITEM'],
        targetRef,
        risk: 'MEDIUM',
        status: 'supported',
        confirmation: 'CONFIRM_BEFORE_RUN',
        requiredAnyPermissions: ['checklist.change_status', 'checklist.manage_proof', 'checklist.assign', 'checklist.comment', 'checklist.edit_canvas'],
        executorHint: 'Use checklist operational permissions and existing status/proof/comment/dependency services.',
        tourFallback: true,
      }),
    )
  }

  for (const form of input.forms ?? []) {
    const active = form.lifecycleStatus === 'ACTIVE'
    const draft = form.lifecycleStatus === 'DRAFT'
    const archived = form.lifecycleStatus === 'ARCHIVED'
    const targetRef: WebIntentTargetRef = {
      id: form.id,
      type: 'FORM',
      title: form.title,
      status: form.lifecycleStatus,
      route: `${base}/forms`,
    }
    intents.push(
      itemIntent(input, {
        id: `form.${form.id}.open`,
        title: `Open ${form.title}`,
        description: `Open form "${form.title}".`,
        family: 'FORM',
        surface: 'FORMS',
        verbs: ['open', 'show', 'preview'],
        examples: [`Open ${form.title}`],
        matcherHints: [form.title, 'form'],
        targetTypes: ['FORM'],
        targetRef,
        risk: 'READ_ONLY',
        status: 'supported',
        confirmation: 'NONE',
        requiredAllPermissions: ['forms.view'],
        availability: dynamicAvailability(!archived, archived ? 'This form is archived. Open the archive or restore it before normal use.' : 'Form can be opened.'),
        executeActionKind: 'NAVIGATION',
        executorHint: 'Open Forms with this form selected.',
        tourFallback: false,
      }),
      itemIntent(input, {
        id: `form.${form.id}.fill`,
        title: `Fill ${form.title}`,
        description: `Fill and submit active form "${form.title}".`,
        family: 'FORM',
        surface: 'FORMS',
        verbs: ['fill', 'submit', 'answer'],
        examples: [`Fill ${form.title}`],
        matcherHints: [form.title, 'submit form'],
        targetTypes: ['FORM'],
        targetRef,
        risk: 'MEDIUM',
        status: active ? 'supported' : 'planned',
        confirmation: 'CONFIRM_BEFORE_RUN',
        requiredAllPermissions: ['forms.submit'],
        availability: dynamicAvailability(active, draft ? 'This form is still a draft. It must be activated before submission.' : 'This form is archived and cannot be submitted.'),
        executeActionKind: 'FORM_SUBMIT',
        executorHint: 'Open the form responder; do not fabricate response values without user confirmation.',
        tourFallback: true,
      }),
      itemIntent(input, {
        id: `form.${form.id}.edit`,
        title: `Edit ${form.title}`,
        description: `Edit form structure for "${form.title}".`,
        family: 'FORM',
        surface: 'FORMS',
        verbs: ['edit', 'change', 'add field', 'lock', 'archive'],
        examples: [`Add a required phone field to ${form.title}`],
        matcherHints: [form.title, 'form builder', 'fields'],
        targetTypes: ['FORM'],
        targetRef,
        risk: 'MEDIUM',
        status: form.isLocked ? 'preview_only' : 'preview_only',
        confirmation: 'SURFACE_APPROVAL',
        requiredAllPermissions: ['forms.design'],
        availability: dynamicAvailability(!archived, archived ? 'This form is archived. Restore it before editing.' : 'Open Forms builder for review.'),
        executorHint: 'Prepare suggested builder changes and let Forms own the save path.',
        tourFallback: true,
      }),
    )
  }

  for (const automation of input.automations ?? []) {
    const targetRef: WebIntentTargetRef = {
      id: automation.id,
      type: 'AUTOMATION',
      title: automation.name,
      status: automation.status,
      route: `${base}/automate?workflow=${encodeURIComponent(automation.id)}`,
    }
    intents.push(
      itemIntent(input, {
        id: `automation.${automation.id}.open`,
        title: `Open ${automation.name}`,
        description: `Open automation "${automation.name}".`,
        family: 'AUTOMATION',
        surface: 'AUTOMATE',
        verbs: ['open', 'show', 'inspect'],
        examples: [`Open automation ${automation.name}`],
        matcherHints: [automation.name, automation.kind ?? 'automation'],
        targetTypes: ['AUTOMATION'],
        targetRef,
        risk: 'READ_ONLY',
        status: 'supported',
        confirmation: 'NONE',
        requiredAllPermissions: ['automation.view'],
        executeActionKind: 'NAVIGATION',
        executorHint: 'Open Automate with this workflow selected.',
        tourFallback: false,
      }),
      itemIntent(input, {
        id: `automation.${automation.id}.edit`,
        title: `Edit ${automation.name}`,
        description: `Edit automation "${automation.name}".`,
        family: 'AUTOMATION',
        surface: 'AUTOMATE',
        verbs: ['edit', 'pause', 'resume', 'change trigger', 'change action'],
        examples: [`Pause automation ${automation.name}`],
        matcherHints: [automation.name, 'workflow edit'],
        targetTypes: ['AUTOMATION'],
        targetRef,
        risk: 'MEDIUM',
        status: 'preview_only',
        confirmation: 'SURFACE_APPROVAL',
        requiredAllPermissions: ['automation.manage'],
        executorHint: 'Open Automate and prepare changes; activation/deletion stays surface-owned.',
        tourFallback: true,
      }),
    )
  }

  for (const meeting of input.meetings ?? []) {
    const cancelled = meeting.status === 'CANCELLED'
    const targetRef: WebIntentTargetRef = {
      id: meeting.id,
      type: 'MEETING',
      title: meeting.title,
      status: meeting.status,
      route: `${base}/meetings`,
    }
    intents.push(
      itemIntent(input, {
        id: `meeting.${meeting.id}.open`,
        title: `Open ${meeting.title}`,
        description: `Open meeting "${meeting.title}".`,
        family: 'MEETING',
        surface: 'MEETINGS',
        verbs: ['open', 'show', 'join'],
        examples: [`Open ${meeting.title}`],
        matcherHints: [meeting.title, 'meeting'],
        targetTypes: ['MEETING'],
        targetRef,
        risk: 'READ_ONLY',
        status: cancelled ? 'planned' : 'supported',
        confirmation: 'NONE',
        requiredAllPermissions: ['meetings.view'],
        availability: dynamicAvailability(!cancelled, 'This meeting is cancelled.'),
        executeActionKind: 'NAVIGATION',
        executorHint: 'Open Meetings with this meeting selected.',
        tourFallback: false,
      }),
      itemIntent(input, {
        id: `meeting.${meeting.id}.manage`,
        title: `Manage ${meeting.title}`,
        description: `Edit or cancel meeting "${meeting.title}".`,
        family: 'MEETING',
        surface: 'MEETINGS',
        verbs: ['edit', 'reschedule', 'cancel'],
        examples: [`Cancel ${meeting.title}`],
        matcherHints: [meeting.title, 'reschedule', 'cancel meeting'],
        targetTypes: ['MEETING'],
        targetRef,
        risk: 'HIGH',
        status: cancelled ? 'planned' : 'supported',
        confirmation: 'CONFIRM_BEFORE_RUN',
        requiredAllPermissions: ['meetings.manage'],
        availability: dynamicAvailability(!cancelled, 'This meeting is already cancelled.'),
        executorHint: 'Use Meetings service/callable paths; cancellation requires confirmation.',
        tourFallback: true,
      }),
    )
  }

  for (const report of input.reports ?? []) {
    const revoked = Boolean(report.revokedAt)
    const targetRef: WebIntentTargetRef = {
      id: report.id,
      type: 'REPORT',
      title: report.title,
      status: revoked ? 'REVOKED' : 'ACTIVE',
      route: `${base}/${board}/reports/${encodeURIComponent(report.id)}`,
    }
    intents.push(
      itemIntent(input, {
        id: `report.${report.id}.open`,
        title: `Open ${report.title}`,
        description: `Open generated report "${report.title}".`,
        family: 'REPORT',
        surface: 'REPORTS',
        verbs: ['open', 'show', 'preview'],
        examples: [`Open report ${report.title}`],
        matcherHints: [report.title, 'report'],
        targetTypes: ['REPORT'],
        targetRef,
        risk: 'READ_ONLY',
        status: revoked ? 'planned' : 'supported',
        confirmation: 'NONE',
        requiredAllPermissions: ['workspace.view_reports'],
        availability: dynamicAvailability(!revoked, 'This report was revoked. Generate or open another version.'),
        executeActionKind: 'NAVIGATION',
        executorHint: 'Open Report Center for this report.',
        tourFallback: false,
      }),
    )
  }

  for (const upload of input.uploads ?? []) {
    const active = upload.status === 'ACTIVE'
    const targetRef: WebIntentTargetRef = {
      id: upload.id,
      type: 'UPLOAD_FILE',
      title: upload.fileName,
      status: upload.status,
      route: `${base}/upload`,
    }
    intents.push(
      itemIntent(input, {
        id: `upload.${upload.id}.open`,
        title: `Open ${upload.fileName}`,
        description: `Open Upload Hub artifact "${upload.fileName}".`,
        family: 'UPLOAD',
        surface: 'UPLOAD',
        verbs: ['open', 'show', 'preview', 'download'],
        examples: [`Open file ${upload.fileName}`],
        matcherHints: [upload.fileName, upload.artifactType, upload.visibility],
        targetTypes: ['UPLOAD_FILE'],
        targetRef,
        risk: 'READ_ONLY',
        status: active ? 'supported' : 'planned',
        confirmation: 'NONE',
        requiredAllPermissions: ['integrations.view'],
        availability: dynamicAvailability(active, 'This file is unavailable or failed to upload.'),
        executeActionKind: 'NAVIGATION',
        executorHint: 'Use Upload Hub file-open service so storage permissions remain authoritative.',
        tourFallback: false,
      }),
    )
  }

  for (const asset of input.designAssets ?? []) {
    const targetRef: WebIntentTargetRef = {
      id: asset.id,
      type: 'DESIGN_ASSET',
      title: asset.title,
      status: asset.boundOutputStatus ?? asset.mode,
      route: `${base}/design`,
    }
    intents.push(
      itemIntent(input, {
        id: `design.${asset.id}.open`,
        title: `Open ${asset.title}`,
        description: `Open design asset "${asset.title}".`,
        family: 'DESIGN',
        surface: 'DESIGN',
        verbs: ['open', 'show', 'preview'],
        examples: [`Open design ${asset.title}`],
        matcherHints: [asset.title, asset.boundIntent ?? asset.mode],
        targetTypes: ['DESIGN_ASSET'],
        targetRef,
        risk: 'READ_ONLY',
        status: 'supported',
        confirmation: 'NONE',
        requiredAllPermissions: ['forms.view'],
        executeActionKind: 'NAVIGATION',
        executorHint: 'Open Design Hub with this asset selected.',
        tourFallback: false,
      }),
    )
  }

  for (const channel of input.chatChannels ?? []) {
    const targetRef: WebIntentTargetRef = {
      id: channel.id,
      type: 'CHAT_CHANNEL',
      title: channel.name,
      status: channel.archived ? 'ARCHIVED' : 'ACTIVE',
      route: `${base}/chat`,
    }
    intents.push(
      itemIntent(input, {
        id: `chat.${channel.id}.open`,
        title: `Open #${channel.name}`,
        description: `Open chat channel #${channel.name}.`,
        family: 'CHAT',
        surface: 'CHAT',
        verbs: ['open', 'show'],
        examples: [`Open #${channel.name}`],
        matcherHints: [channel.name, 'chat channel'],
        targetTypes: ['CHAT_CHANNEL'],
        targetRef,
        risk: 'READ_ONLY',
        status: channel.isReadable === false ? 'planned' : 'supported',
        confirmation: 'NONE',
        requiredAllPermissions: ['chat.view'],
        availability: dynamicAvailability(channel.isReadable !== false && channel.archived !== true, channel.archived ? 'This channel is archived.' : 'You do not have access to this channel.'),
        executeActionKind: 'NAVIGATION',
        executorHint: 'Open Chat with this channel selected.',
        tourFallback: false,
      }),
      itemIntent(input, {
        id: `chat.${channel.id}.send`,
        title: `Post to #${channel.name}`,
        description: `Post a confirmed message to #${channel.name}.`,
        family: 'CHAT',
        surface: 'CHAT',
        verbs: ['post', 'send', 'message'],
        examples: [`Post an update to #${channel.name}`],
        matcherHints: [channel.name, 'send chat'],
        targetTypes: ['CHAT_CHANNEL'],
        targetRef,
        risk: 'MEDIUM',
        status: channel.archived ? 'planned' : 'preview_only',
        confirmation: 'CONFIRM_BEFORE_RUN',
        requiredAllPermissions: ['chat.send'],
        availability: dynamicAvailability(channel.archived !== true, 'This channel is archived. Restore it before posting.'),
        executeActionKind: 'CHAT_SEND',
        executorHint: 'Use the existing chat send path after exact message confirmation.',
        tourFallback: true,
      }),
    )
  }

  for (const resource of input.resources ?? []) {
    const surface: AuthenticatedWebSurface = resource.kind === 'STOCK_ITEM' ? 'STOCK' : resource.kind === 'BUDGET_ITEM' ? 'BUDGET' : 'QUOTA'
    intents.push(itemIntent(input, {
      id: `resource.${resource.kind.toLowerCase()}.${resource.id}.open`,
      title: `Open ${resource.title}`,
      description: `Open ${resource.kind.toLowerCase().replace(/_/g, ' ')} "${resource.title}".`,
      family: 'RESOURCE',
      surface,
      verbs: ['open', 'show', 'inspect', 'update'],
      examples: [`Open ${resource.title}`],
      matcherHints: [resource.title, resource.kind],
      targetTypes: [resource.kind],
      targetRef: {
        id: resource.id,
        type: resource.kind,
        title: resource.title,
        status: resource.status,
        route: `${base}/${surface.toLowerCase()}`,
      },
      risk: 'READ_ONLY',
      status: 'planned',
      confirmation: 'SURFACE_APPROVAL',
      requiredAllPermissions: ['timeline.view'],
      executeActionKind: 'NAVIGATION',
      executorHint: 'Open the owning resource surface; mutation adapter is intentionally not implied yet.',
      tourFallback: true,
    }))
  }

  return intents
}

export const listWebIntentCatalog = (): WebIntentDefinition[] => [...WEB_INTENT_CATALOG]

export const getWebIntentById = (id: string): WebIntentDefinition | undefined =>
  WEB_INTENT_CATALOG.find((intent) => intent.id === id)

export const listWebIntentsForSurface = (surface: AuthenticatedWebSurface): WebIntentDefinition[] =>
  WEB_INTENT_CATALOG.filter((intent) => intent.surface === surface)

const MODULE_TITLES: Record<ModuleKey, string> = {
  stock: 'Stock',
  budget: 'Budget',
  quota: 'Quota',
  forms: 'Forms',
  meetings: 'Meetings',
  automate: 'Automate',
  linkHub: 'Link Hub',
  upload: 'Upload',
  design: 'Design',
  analysis: 'Analysis',
  reports: 'Reports',
  funPack: 'Fun pack',
  teaching: 'Teaching',
  training: 'Training',
  clientOutreach: 'Client outreach',
  construction: 'Construction',
  hospital: 'Hospital',
  sites: 'Sites',
  timesheets: 'Timesheets',
  delivery: 'Delivery',
  shops: 'Shops',
  stays: 'Stays',
  jobs: 'Jobs',
  clinics: 'Clinics',
  plant: 'Plant',
}

export const resolveWebIntentAvailability = (
  intent: WebIntentDefinition,
  context: WebIntentPermissionContext,
): WebIntentAvailability => {
  const missingAll = (intent.requiredAllPermissions ?? []).filter((permission) => !context.canPerform(permission))
  const any = intent.requiredAnyPermissions ?? []
  const missingAny = any.length > 0 && !any.some((permission) => context.canPerform(permission)) ? any : []
  const missingPermissions = [...missingAll, ...missingAny]
  if (missingPermissions.length > 0) {
    return {
      state: 'needs_permission',
      message: `This intent needs ${missingPermissions.join(' or ')} permission before it can run.`,
      missingPermissions,
    }
  }
  const moduleKey = moduleForLayoutView(intent.surface)
  if (moduleKey && context.switchedOffModules?.has(moduleKey)) {
    const title = MODULE_TITLES[moduleKey] ?? surfaceTitle(intent.surface)
    return {
      state: 'unavailable',
      message: `${title} is turned off in this workspace.`,
    }
  }
  const checkHidden = context.isPageHidden ?? isPageHiddenForMe
  if (checkHidden(intent.surface)) {
    return {
      state: 'unavailable',
      message: 'unavailable: hidden for you',
    }
  }
  if (intent.availability && intent.availability.state !== 'available') return intent.availability
  if (intent.status === 'planned') {
    return {
      state: 'unsupported',
      message: 'This intent is cataloged but does not have a safe execution adapter yet.',
    }
  }
  return intent.availability ?? { state: 'available', message: 'Intent is available.' }
}

export const isExcludedFromAuthenticatedWebIntents = (pathname: string): boolean => {
  if (!pathname) return true
  return EXCLUDED_WEB_INTENT_ROUTE_PREFIXES.some((prefix) =>
    prefix === '/' ? pathname === '/' : pathname === prefix || pathname.startsWith(`${prefix}/`),
  )
}
